import type {z} from "zod/v4-mini"
import type {ToneMapping} from "#template/nodes/types"
import {
    contrastToneMapping,
    coronaToneMapping,
    filmicAdvancedToneMapping,
    filmicToneMapping,
    hueSaturationMapping,
    linearToneMapping,
    pbrNeutralToneMapping,
    rgbCurveMapping,
} from "#template/nodes/validation"

export function defaultsForToneMapping(mode: ToneMapping["mode"]): ToneMapping {
    if (mode === "filmic") return {mode: "filmic"}
    else if (mode === "filmic-advanced") return {mode, contrast: 1, balance: 0.5, colorBalance: 0}
    else if (mode === "linear") return {mode}
    else if (mode === "contrast") return {mode, contrast: 1, balance: 0.5, colorBalance: 0}
    else if (mode === "corona") return {mode, highlightCompression: 1, contrast: 1, saturation: 0}
    else if (mode === "pbr-neutral") return {mode}
    else if (mode === "rgbCurve") return {mode, parameters: {fac: 1}}
    else if (mode === "hueSaturation") return {mode, parameters: {fac: 1, hue: 0, saturation: 0, value: 0}}
    else throw new Error("Invalid tone mapping mode")
}

export function schemaForToneMapping(mode: ToneMapping["mode"]): z.ZodMiniType<ToneMapping, ToneMapping> {
    if (mode === "filmic") return filmicToneMapping
    else if (mode === "filmic-advanced") return filmicAdvancedToneMapping
    else if (mode === "linear") return linearToneMapping
    else if (mode === "contrast") return contrastToneMapping
    else if (mode === "corona") return coronaToneMapping
    else if (mode === "pbr-neutral") return pbrNeutralToneMapping
    else if (mode === "rgbCurve") return rgbCurveMapping
    else if (mode === "hueSaturation") return hueSaturationMapping
    else throw new Error("Invalid tone mapping mode")
}

// Top-level parameter keys (excluding "mode") for the given tone-mapping mode. `rgbCurve` is a
// `z.custom` schema with no exposed shape, so it returns []; the inspector renders rgbCurve via a
// dedicated UI rather than enumerated key slots.
export function parameterKeysForToneMapping(mode: ToneMapping["mode"]): readonly string[] {
    if (mode === "filmic-advanced") return Object.keys(filmicAdvancedToneMapping.shape).filter((k) => k !== "mode")
    if (mode === "contrast") return Object.keys(contrastToneMapping.shape).filter((k) => k !== "mode")
    if (mode === "corona") return Object.keys(coronaToneMapping.shape).filter((k) => k !== "mode")
    if (mode === "hueSaturation") return Object.keys(hueSaturationMapping.shape).filter((k) => k !== "mode")
    return []
}
